import React, { useContext } from 'react';
import styled from 'styled-components';
import { t } from '@lingui/macro';
import Link from 'next/link';
import { Trans } from '@lingui/react';
import { useMedia } from 'use-media';
import { Box, Flex } from '@components/layout/Grid';
import { FavoriteVideoButton } from '@components/ButtonFavoriteVideoCard';
import { TaxonomyV2FieldsFragment, Term, VideoQuery } from '@gql/generated';
import { H4 } from '@components/typography/Headings';
import Divider from '@components/Divider';
import InstructorCard, {
  SmallWrapper,
  Thumbnail,
} from '@organisms/cards/InstructorCard';
import LanguageContext from '@lib/contexts/languageContext';
import ButtonAddToPlaylist from './ButtonAddtoPlaylist';
import ButtonScheduleModal from './ButtonScheduleModal';
import MenuItemShareModal from './MenuItemShareModal';
import Icon from '@components/Icon';
import {
  Popover,
  PopoverContent,
  PopoverItem,
  PopoverTrigger,
} from 'src/ui/popover';
import { Url } from 'next/dist/shared/lib/router/router';

const Title = styled.h1`
  font-size: 24px;
  line-height: 32px;
  font-weight: bold;
  color: ${(props) => props.theme.colors.neutrals.black};
  margin: 0;
`;

const DescriptionContainer = styled(Box)`
  margin-bottom: 50px;
  color: ${(props) => props.theme.colors.neutrals.black};
`;

const Instructors = styled(Flex)`
  gap: 5px;
`;

const ActionWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  align-items: center;

  margin: 20px 0;

  background-color: #f2f2f2;
  padding: 0 25px;

  > *:not(:last-child) {
    margin-right: 25px;
  }

  @media (max-width: ${(props) => `${props.theme.viewports.tablet}px`}) {
    padding: 0 10px;
    margin: 0;
  }
`;

export const ActionButton = styled.button`
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 14px;
  border: none;
  padding: 20px 10px;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  background-color: transparent;
  transition: background-color 0.1s ease-in-out;

  &:hover {
    background-color: #e8e8e8;
  }

  > i {
    margin-right: 8px;
  }

  @media (min-width: ${(props) => `${props.theme.viewports.tablet}px`}) {
    font-size: 16px;
  }
`;

const ChipWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  gap: 8px;
`;

const Chip = styled.div`
  padding: 6px 10px;
  border-radius: 5px;
  user-select: none;
  cursor: pointer;
  font-size: 16px;

  white-space: nowrap;

  color: ${(props) => props.theme.colors.neutrals.black} !important;

  /* transition both background-color and color */
  transition:
    background-color 0.05s ease-in-out,
    color 0.05s ease-in-out;

  background-color: #eaeaea;

  &:hover {
    background-color: #667c91;
    color: white !important;
  }

  display: flex;
  flex-direction: row;
  align-items: center;

  .material-icons {
    margin-right: 10px;
  }

  @media (max-width: ${(props) => `${props.theme.viewports.tablet}px`}) {
    font-size: 14px;
  }
`;

const TitleWrapper = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: row;

  align-items: flex-start;
  justify-content: space-between;

  margin-bottom: 20px;

  ${SmallWrapper} {
    flex-direction: row-reverse;

    > * {
      margin-right: 0;
    }

    > *:not(:first-child) {
      margin-right: 20px;
    }
  }

  @media (max-width: ${(props) => `${props.theme.viewports.tablet}px`}) {
    flex-direction: column;
    align-items: flex-start;

    ${Title} {
      margin-bottom: 5px;
    }

    ${SmallWrapper} {
      align-items: flex-start;
      color: ${(props) => props.theme.colors.primary['blue-1']};
      flex-direction: column;

      ${Thumbnail} {
        display: none;
      }
    }
  }
`;

const InstructorCardWrapper = styled.div`
  border: 1px solid #d7d7d7;
  border-radius: 3px;
  margin-bottom: 10px;
  padding: 20px;
`;

const Description = styled.p`
  @media (max-width: ${(props) => `${props.theme.viewports.tablet}px`}) {
    font-size: 14px;
  }
`;

const ChipWrapperTitle = styled.p`
  margin: 0;
`;

interface Props {
  id: string;
  identifier: string;
  title: string;
  description: string;
  duration: number;
  instructors: Pick<
    VideoQuery['video']['taxonomies']['instructors'][number],
    'id' | 'name' | 'slug' | 'assets' | 'shortDescription'
  >[];
  primaryStyle?: Pick<Term, 'slug' | 'name'>;
  taxonomies: TaxonomyV2FieldsFragment[];
}

const MoreButton = (props) => {
  const isMobile = useMedia({ maxWidth: '769px' });

  return (
    <Popover>
      <PopoverTrigger className="flex items-center text-base font-semibold hover:bg-neutral-200 px-3 py-5 mr-0 ml-auto">
        <Icon className="mr-2" name="ygb-icon-menu-verb" fontSize="16px" />
        <span className="first-letter:capitalize">{t`metadata.more`}</span>
      </PopoverTrigger>
      <PopoverContent className="text-base">
        {isMobile && (
          <ButtonAddToPlaylist tag={PopoverItem} videoId={props.id} />
        )}
        <ButtonScheduleModal
          tag={PopoverItem}
          resource="video"
          resourceId={props.id}
        />
        <MenuItemShareModal
          resourceIdentifier={props.identifier}
          resource={'videos'}
        />
      </PopoverContent>
    </Popover>
  );
};

const VideoSingleMetadata = (props: Props) => {
  const locale = useContext(LanguageContext);
  const isMobile = useMedia({ maxWidth: '769px' });

  return (
    <Box
      alignItems="flex-start"
      data-testid="VideoSingleMetadata"
      pt={20}
      flex={1}
    >
      <TitleWrapper>
        <Box flex={1} mr={20}>
          <Title data-testid="VideoSingleMetadata--Title">{props.title}</Title>
        </Box>
      </TitleWrapper>
      <Instructors alignItems="center" mb={20}>
        {props.instructors.map((instructor, i, arr) => {
          return (
            <React.Fragment key={instructor.id}>
              {i > 0 ? (
                <span>
                  <Trans id="metadata.and" />
                </span>
              ) : null}
              <InstructorCard
                name={instructor.name}
                slug={instructor.slug}
                thumbnailUrl={
                  arr.length > 1 ? null : instructor.assets?.squareImage
                }
                variant="small"
              />
            </React.Fragment>
          );
        })}
      </Instructors>
      <ActionWrapper>
        <FavoriteVideoButton videoId={props.id} />
        {!isMobile ? (
          <ButtonAddToPlaylist
            tag={({ onClick, children }) => (
              <ActionButton onClick={onClick}>{children}</ActionButton>
            )}
            videoId={props.id}
          />
        ) : null}
        <MoreButton id={props.id} identifier={props.identifier} />
      </ActionWrapper>

      <Box
        flex={['1 1 100%', '1 1 100%', '0 calc(40% - 30px)']}
        mt={[25, 25, 40]}
        mb={[25, 25, 0]}
      >
        {/* Taxanomy section */}
        <div className="grid grid-cols-3 gap-2">
          <ChipWrapperTitle className="text-sm md:text-base">
            <Trans
              id="video.single.metadata.session_info"
              message="Session Info"
            />
          </ChipWrapperTitle>
          <ChipWrapper className="col-span-2">
            <Link
              href={{
                pathname: `/${locale}/videos/genres/all`,
                query: { durations: props.duration },
              }}
              prefetch={false}
            >
              <Chip>{props.duration + t`metadata.min`}</Chip>
            </Link>
          </ChipWrapper>
          {/* {!!props.primaryStyle ? (
            <>
              <ChipWrapperTitle>
                <Trans
                  id="video.single.metadata.primary_style"
                  message="Primary style"
                />
              </ChipWrapperTitle>
              <ChipWrapper className="col-span-2">
                {props.primaryStyle ? (
                  <Link
                    href={{
                      pathname: `/${locale}/videos/genres/all`,
                      query: { styles: props.primaryStyle.slug },
                    }}
                    prefetch={false}
                  >
                    <Chip>{props.primaryStyle.name}</Chip>
                  </Link>
                ) : null}
              </ChipWrapper>
            </>
          ) : null} */}
          {props.taxonomies.map((taxonomyV2) => {
            if (!taxonomyV2.term) {
              return null;
            }
            return (
              <React.Fragment key={taxonomyV2.term.slug}>
                <ChipWrapperTitle className="text-sm md:text-base first-letter:capitalize">
                  {taxonomyV2?.term.name}
                </ChipWrapperTitle>
                <ChipWrapper className="col-span-2">
                  {taxonomyV2.taxonomies.map((taxonomy) => {
                    let href: Url = {
                      pathname: `/${locale}/videos/genres/all`,
                      query: {
                        [taxonomyV2?.term.slug]: taxonomy.slug,
                      },
                    };

                    const tag = (
                      <Chip key={taxonomy.slug}>{taxonomy.name}</Chip>
                    );

                    if (taxonomyV2.term.slug === 'accessories') {
                      href = taxonomy?.link ? { pathname: taxonomy.link } : '#';
                      if (!taxonomy?.link) {
                        return tag;
                      }
                    }

                    return (
                      <Link
                        key={taxonomy.slug}
                        target={taxonomy?.link ? '_blank' : ''}
                        href={href}
                        prefetch={false}
                      >
                        {tag}
                      </Link>
                    );
                  })}
                </ChipWrapper>
              </React.Fragment>
            );
          })}
        </div>
      </Box>
      <Box my={[25, 25, 40]}>
        <Divider />
      </Box>
      <DescriptionContainer
        className="embeddedText"
        data-testid="VideoSingleMetadata--Desc"
      >
        <H4 bold>
          <Trans
            id="video.single.metadata.about"
            message="About this session"
          />
        </H4>
        <Description dangerouslySetInnerHTML={{ __html: props.description }} />
      </DescriptionContainer>
      {props.instructors && props.instructors.length > 0 ? (
        <DescriptionContainer className="embeddedText">
          <H4 bold>
            <Trans
              id="video.single.metadata.about_instructor"
              message="About the instructor"
            />
          </H4>
          {props.instructors.map((instructor, i) => (
            <InstructorCardWrapper key={i}>
              <InstructorCard
                key={instructor.id}
                name={instructor.name}
                description={instructor.shortDescription}
                thumbnailUrl={instructor.assets.squareImage}
                slug={instructor.slug}
              />
            </InstructorCardWrapper>
          ))}
        </DescriptionContainer>
      ) : null}
    </Box>
  );
};

export default VideoSingleMetadata;
